import { useBoard } from './store';
import { useCamera } from './camera';
import { useEditorUI } from './ui';
import { hexA, type AssetInfo } from '@scribbleplot/shared';
import { color, font } from '../tokens';
import { Button } from '../ui/primitives';
import { beginAssetDrag } from './Canvas';

/**
 * Right rail: uploaded images plus a set of built-in placeholders.
 *
 * Placeholders matter more than they look: they let someone block out a layout
 * before they have any real assets, which is the whole point of a rough mock.
 */

export type Placeholder = { name: string; w: number; h: number; src: string };

export function AssetsPanel({
  assets,
  placeholders,
  accent,
  canEdit,
  uploading,
  unavailable = false,
  onPickImage,
  onDeleteAsset,
}: {
  assets: AssetInfo[];
  placeholders: Placeholder[];
  accent: string;
  canEdit: boolean;
  uploading: boolean;
  unavailable?: boolean;
  onPickImage: () => void;
  onDeleteAsset: (id: string) => void;
}) {
  const place = (asset: { src: string; w: number; h: number; name: string; assetId?: string }) => {
    if (!canEdit) return;
    const camera = useCamera.getState();
    const rect = camera.viewport?.getBoundingClientRect();
    if (!rect) return;
    const [x, y] = camera.toWorld(rect.left + rect.width / 2, rect.top + rect.height / 2);
    useBoard.getState().addImage(asset.src, asset.w, asset.h, x, y, asset.name, asset.assetId);
    useEditorUI.getState().open(null);
  };
  const tile = (src: string) => ({
    aspectRatio: '4 / 3',
    borderRadius: 10,
    border: `1px solid ${color.lineSoft}`,
    background: '#202127',
    backgroundImage: `url("${src}")`,
    backgroundSize: 'cover' as const,
    backgroundPosition: 'center' as const,
  });

  const caption = {
    fontSize: 10.5,
    fontFamily: font.mono,
    color: color.textDim,
    marginTop: 4,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div
      style={{
        width: 250,
        flex: 'none',
        borderLeft: `1px solid ${color.line}`,
        background: color.surfaceAlt,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '13px 14px 6px',
          flex: 'none',
        }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>Assets</div>
        {canEdit && (
          <Button
            onClick={onPickImage}
            disabled={uploading}
            hoverStyle={{ filter: 'brightness(1.15)' }}
            style={{
              height: 26,
              padding: '0 11px',
              borderRadius: 8,
              border: 'none',
              background: hexA(accent, 0.16),
              color: accent,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {uploading ? 'Adding…' : '+ Add'}
          </Button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 14px 20px' }}>
        <div
          style={{
            fontSize: 10,
            fontFamily: font.mono,
            letterSpacing: '.12em',
            color: color.textFaint,
            margin: '8px 0',
          }}
        >
          UPLOADS
        </div>
        {!unavailable && !assets.length && (
          <div style={{ fontSize: 12, color: color.textGhost, lineHeight: 1.5 }}>
            Nothing yet — add images, or drop files straight onto the canvas.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {assets.map((asset) => (
            <div key={asset.id} style={{ cursor: canEdit ? 'grab' : 'default', position: 'relative' }}>
              <div
                role="button"
                tabIndex={canEdit ? 0 : -1}
                aria-label={`Place ${asset.name}`}
                title={`${asset.name} — drag onto the canvas`}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    place({ src: asset.url, w: asset.w, h: asset.h, name: asset.name, assetId: asset.id });
                  }
                }}
                onClick={() => {
                  if (window.matchMedia('(pointer: coarse)').matches)
                    place({ src: asset.url, w: asset.w, h: asset.h, name: asset.name, assetId: asset.id });
                }}
                onPointerDown={(event) =>
                  event.pointerType !== 'touch' &&
                  canEdit &&
                  beginAssetDrag({ src: asset.url, w: asset.w, h: asset.h, name: asset.name, assetId: asset.id }, event)
                }
                style={tile(asset.url)}
              />
              <div style={caption}>{asset.name}</div>
              {canEdit && (
                <button
                  type="button"
                  title="Remove from assets"
                  aria-label={`Remove ${asset.name} from assets`}
                  onClick={() => onDeleteAsset(asset.id)}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(20,21,25,.82)',
                    color: color.textMuted,
                    fontSize: 13,
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        <div
          style={{
            fontSize: 10,
            fontFamily: font.mono,
            letterSpacing: '.12em',
            color: color.textFaint,
            margin: '18px 0 8px',
          }}
        >
          PLACEHOLDERS
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {placeholders.map((item) => (
            <div key={item.name} style={{ cursor: canEdit ? 'grab' : 'default' }}>
              <div
                role="button"
                tabIndex={canEdit ? 0 : -1}
                aria-label={`Place ${item.name} placeholder`}
                title={`${item.name} — drag onto the canvas`}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    place(item);
                  }
                }}
                onClick={() => {
                  if (window.matchMedia('(pointer: coarse)').matches) place(item);
                }}
                onPointerDown={(event) => event.pointerType !== 'touch' && canEdit && beginAssetDrag(item, event)}
                style={tile(item.src)}
              />
              <div style={caption}>{item.name}</div>
            </div>
          ))}
        </div>

        {canEdit && (
          <div style={{ fontSize: 11, color: color.textGhost, lineHeight: 1.55, marginTop: 16 }}>
            Drag a tile onto the canvas, or click it to drop at center.
          </div>
        )}
      </div>
    </div>
  );
}

/** Inline placeholder artwork, generated once at module load. */
export const PLACEHOLDERS: Placeholder[] = [
  { name: 'image', w: 560, h: 400 },
  { name: 'hero shot', w: 840, h: 480 },
  { name: 'portrait', w: 420, h: 560 },
  { name: 'texture', w: 480, h: 480 },
  { name: 'logo', w: 360, h: 240 },
  { name: 'palette', w: 480, h: 280 },
].map(({ name, w, h }) => ({ name, w, h, src: placeholderSvg(name, w, h) }));

function placeholderSvg(label: string, w: number, h: number): string {
  const fontSize = Math.max(13, Math.round(Math.min(w, h) / 14));
  const escaped = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'>` +
    `<defs><pattern id='p' width='14' height='14' patternTransform='rotate(45)' patternUnits='userSpaceOnUse'>` +
    `<rect width='14' height='14' fill='#26272E'/><rect width='7' height='14' fill='#2B2C34'/></pattern></defs>` +
    `<rect width='100%' height='100%' fill='url(#p)'/>` +
    `<rect x='.5' y='.5' width='${w - 1}' height='${h - 1}' fill='none' stroke='#3A3C46'/>` +
    `<text x='50%' y='50%' fill='#8E93A3' font-family='Space Mono,monospace' font-size='${fontSize}' ` +
    `text-anchor='middle' dominant-baseline='middle'>${escaped}</text></svg>`;
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}
